import type { ComponentProps } from "react";
import Link from "next/link";
import { cx } from "./cx";
import { Wordmark } from "./wordmark";
import "./brand-lockup.css";

/**
 * The brand mark's map-pin badge: a rounded teal square holding the classic
 * teardrop pin. All color comes from CSS tokens (currentColor + fill rules),
 * never literals. Exported so callers can compose it directly.
 */
export function BrandPin() {
  return (
    <span className="pz-siteheader__pinbox" aria-hidden>
      <svg className="pz-siteheader__pinicon" viewBox="0 0 34 44" fill="none">
        <path
          d="M17 42.5C17 42.5 3.5 24.8 3.5 15.2C3.5 7.7 9.5 1.8 17 1.8C24.5 1.8 30.5 7.7 30.5 15.2C30.5 24.8 17 42.5 17 42.5Z"
          fill="currentColor"
        />
        <circle className="pz-siteheader__pinhole" cx="17" cy="15.2" r="5" />
      </svg>
    </span>
  );
}

export interface BrandLockupProps extends Omit<ComponentProps<typeof Link>, "className" | "children"> {
  /** Brand display name (wordmark alt text) — from the white-label config. */
  brandName: string;
  /** Wordmark contract from the theme: text + optional logo asset. */
  wordmark: { text: string; logoUrl: string | null };
  className?: string;
}

/**
 * The brand lockup (pin badge + wordmark) as a single link, shared by the
 * marketing pill-nav `SiteHeader`, the in-app `AppChrome`/editor Topbar
 * (MAP-106), and the public map viewer's growth banner. Kept in its OWN
 * Clerk-free module (not on `site-chrome`, which imports `SiteNav` →
 * `@clerk/nextjs`) so the lean viewer/embed bundle can reuse the exact mark
 * without pulling Clerk in. Link props (`href`, `target`, `rel`, `onClick`, …)
 * pass straight through.
 */
export function BrandLockup({ brandName, wordmark, className, ...rest }: BrandLockupProps) {
  return (
    <Link className={cx("pz-siteheader__brand", className)} {...rest}>
      <BrandPin />
      <Wordmark brandName={brandName} wordmark={wordmark} />
    </Link>
  );
}
